import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Truck, Phone, FileText, Wrench, Fuel, Receipt, Route, Loader2,
} from "lucide-react";
import { useVehicleProfile } from "../hooks/useVehicleProfile";

const DOC_TYPE_LABELS = { rc: "RC", insurance: "Insurance", permit: "Permit", puc: "PUC", fitness: "Fitness" };
const CATEGORY_LABELS = { toll: "Toll", repair: "Repair", insurance_premium: "Insurance Premium", salary: "Driver Salary", other: "Other" };
const TRIP_STATUS_COLORS = {
  planned: { color: "#2563EB", bg: "#EFF6FF" },
  in_progress: { color: "#D97706", bg: "#FFFBEB" },
  completed: { color: "#16A34A", bg: "#F0FDF4" },
  cancelled: { color: "#DC2626", bg: "#FEF2F2" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function daysStatus(iso) {
  if (!iso) return { text: "No date set", color: "#6B7280", bg: "#F3F4F6" };
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, color: "#DC2626", bg: "#FEF2F2" };
  if (days <= 30) return { text: `${days}d left`, color: "#D97706", bg: "#FFFBEB" };
  return { text: fmtDate(iso), color: "#16A34A", bg: "#F0FDF4" };
}

function computeMileage(fuelLogs) {
  const asc = [...fuelLogs].sort((a, b) => new Date(a.filledAt) - new Date(b.filledAt));
  const mileage = {};
  for (let i = 1; i < asc.length; i++) {
    const prev = asc[i - 1], cur = asc[i];
    if (prev.odometerKm != null && cur.odometerKm != null && cur.liters > 0) {
      const distance = cur.odometerKm - prev.odometerKm;
      if (distance > 0) mileage[cur.id] = distance / cur.liters;
    }
  }
  return mileage;
}

function Chip({ text, color, bg }) {
  return (
    <span style={{
      display: "inline-block", fontSize: 10.5, fontWeight: 700,
      color, background: bg, borderRadius: 99, padding: "2px 9px", whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

function Section({ icon: Icon, title, accent, count, onManage, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: accent + "1A", color: accent,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}><Icon size={15} /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{title}</div>
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>({count})</span>
        </div>
        <button onClick={onManage} style={{ background: "none", border: "none", color: "#4F46E5", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Manage →
        </button>
      </div>
      {children}
    </div>
  );
}

function Row({ left, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, padding: "7px 0", borderTop: "1px solid #F3F4F6" }}>
      <span style={{ color: "#111827" }}>{left}</span>
      {right}
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ fontSize: 13, color: "#9CA3AF", padding: "8px 0" }}>{text}</div>;
}

export default function VehicleProfile({ vehicle, onClose, onNavigate }) {
  const { data, loading } = useVehicleProfile(vehicle?.id);
  const mileageMap = useMemo(() => (data ? computeMileage(data.fuelLogs) : {}), [data]);

  function goTo(tab) {
    onNavigate?.(tab);
    onClose?.();
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{ position: "fixed", inset: 0, zIndex: 850, background: "#F9FAFB", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0, background: "#fff", borderBottom: "1px solid #E5E7EB",
          padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, background: "#EEF2FF", color: "#4F46E5",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}><Truck size={20} /></div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>
                {vehicle?.nickname || vehicle?.plateNumber}
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 12.5, color: "#6B7280", marginTop: 2 }}>
                <span>{vehicle?.plateNumber}</span>
                <span>{vehicle?.type}</span>
                {vehicle?.driverName && (
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {vehicle.driverName}
                    {vehicle.driverPhone && <><Phone size={11} /> {vehicle.driverPhone}</>}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 36, height: 36, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
          ><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px", display: "flex", flexDirection: "column", gap: 14 }}>
            {loading || !data ? (
              <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading vehicle profile…
              </div>
            ) : (
              <>
                <Section icon={FileText} title="Documents" accent="#4F46E5" count={data.documents.length} onManage={() => goTo("documents")}>
                  {data.documents.length === 0 ? <Empty text="No documents on file." /> : data.documents.map(d => {
                    const s = daysStatus(d.expiryDate);
                    return <Row key={d.id} left={`${DOC_TYPE_LABELS[d.docType]}${d.docNumber ? ` — ${d.docNumber}` : ""}`} right={<Chip text={s.text} color={s.color} bg={s.bg} />} />;
                  })}
                </Section>

                <Section icon={Wrench} title="Maintenance" accent="#D97706" count={data.maintenance.length} onManage={() => goTo("maintenance")}>
                  {data.maintenance.length === 0 ? <Empty text="No maintenance records." /> : data.maintenance.map(m => {
                    const s = m.status === "completed"
                      ? { text: "Completed", color: "#16A34A", bg: "#F0FDF4" }
                      : daysStatus(m.dueDate);
                    return <Row key={m.id} left={m.serviceType} right={<Chip text={s.text} color={s.color} bg={s.bg} />} />;
                  })}
                </Section>

                <Section icon={Fuel} title="Fuel Logs" accent="#2563EB" count={data.fuelLogs.length} onManage={() => goTo("fuel")}>
                  {data.fuelLogs.length === 0 ? <Empty text="No fuel logs." /> : data.fuelLogs.slice(0, 8).map(f => (
                    <Row key={f.id}
                      left={`${fmtDate(f.filledAt)} — ${f.liters ?? "—"} L — ₹${(f.cost ?? 0).toLocaleString("en-IN")}`}
                      right={mileageMap[f.id] != null ? <Chip text={`${mileageMap[f.id].toFixed(1)} km/l`} color="#2563EB" bg="#EFF6FF" /> : null}
                    />
                  ))}
                </Section>

                <Section icon={Receipt} title="Expenses" accent="#059669" count={data.expenses.length} onManage={() => goTo("expenses")}>
                  {data.expenses.length === 0 ? <Empty text="No expenses logged." /> : data.expenses.slice(0, 8).map(e => (
                    <Row key={e.id} left={`${CATEGORY_LABELS[e.category]} — ${fmtDate(e.expenseDate)}`} right={<span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>₹{e.amount.toLocaleString("en-IN")}</span>} />
                  ))}
                </Section>

                <Section icon={Route} title="Trip History" accent="#16A34A" count={data.trips.length} onManage={() => goTo("trips")}>
                  {data.trips.length === 0 ? <Empty text="No trips recorded." /> : data.trips.slice(0, 8).map(t => {
                    const s = TRIP_STATUS_COLORS[t.status] || TRIP_STATUS_COLORS.planned;
                    return <Row key={t.id} left={t.label || `${t.source.label || "Origin"} → ${t.destination.label || "Destination"}`} right={<Chip text={t.status.replace("_", " ")} color={s.color} bg={s.bg} />} />;
                  })}
                </Section>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
